/**
 * Composable for external browser window management
 *
 * Responsibilities:
 * - Opens real browser windows with window.open()
 * - Manages open window references
 * - External windows are INDEPENDENT from main window
 * - When user changes view in main window → external window stays open
 * - User can manually close external window or switch to another view
 */

// Global window references (per entity)
const externalWindows = new Map<string, Window | null>();

export function useExternalWindow() {
  /**
   * Opens a real external window
   * @param entityKey e.g. "action-cost-statements"
   * @param url the URL to open
   * @param options window.open() options (width, height, left, top)
   */
  const openExternalWindow = (
    entityKey: string,
    url: string,
    options: {
      width?: number;
      height?: number;
      left?: number;
      top?: number;
    } = {},
  ) => {
    const {
      width = options.width ?? 1000,
      height = options.height ?? 700,
      left = options.left ?? window.screenX + 100,
      top = options.top ?? window.screenY + 100,
    } = options;

    // Window specs for window.open()
    const specs = `
      width=${width},
      height=${height},
      left=${left},
      top=${top},
      resizable=yes,
      scrollbars=yes
    `
      .split(',')
      .map((s) => s.trim())
      .join(',');

    try {
      const externalWindow = window.open(url, entityKey, specs);

      if (externalWindow) {
        externalWindows.set(entityKey, externalWindow);
        return externalWindow;
      } else {
        return null;
      }
    } catch (_error) {
      return null;
    }
  };

  /**
   * Closes an external window
   */
  const closeExternalWindow = (entityKey: string) => {
    const win = externalWindows.get(entityKey);
    if (win && !win.closed) {
      console.log(`[useExternalWindow] Closing external window for "${entityKey}"`);
      win.close();
      externalWindows.set(entityKey, null);
    }
  };

  /**
   * Checks if an external window is still open
   */
  const isExternalWindowOpen = (entityKey: string): boolean => {
    const win = externalWindows.get(entityKey);
    return win !== null && !win!.closed;
  };

  /**
   * Returns the window reference
   */
  const getExternalWindow = (entityKey: string): Window | null => {
    const win = externalWindows.get(entityKey);
    return win && !win.closed ? win : null;
  };

  return {
    openExternalWindow,
    closeExternalWindow,
    isExternalWindowOpen,
    getExternalWindow,
  };
}
